import React from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import HypothesisCard from './HypothesisCard';
import PermissionGate from '../PermissionGate';
import { Hypothesis } from '../../types';

interface HypothesesListProps {
  hypotheses: Hypothesis[];
  problems?: Record<string, string>;
  loading?: boolean;
  emptyMessage?: string;
  showProblemTitles?: boolean;
  showProjectContext?: boolean;
  filterProblemId?: string;
  showCreateButton?: boolean;
  createButtonText?: string;
  onHypothesisClick?: (hypothesis: Hypothesis) => void;
  onHypothesisUpdate?: (hypothesis: Hypothesis) => void;
}

const HypothesesList: React.FC<HypothesesListProps> = ({
  hypotheses,
  problems = {},
  loading = false,
  emptyMessage = "No hypotheses found.",
  showProblemTitles = false,
  showProjectContext = false,
  filterProblemId,
  showCreateButton = true,
  createButtonText = "Create New Hypothesis",
  onHypothesisClick,
  onHypothesisUpdate
}) => {
  const navigate = useNavigate();

  // Filter hypotheses if filterProblemId is provided
  const filteredHypotheses = filterProblemId 
    ? hypotheses.filter(h => h.problem_id === filterProblemId)
    : hypotheses;

  // Sort by priority score
  const sortedHypotheses = [...filteredHypotheses].sort((a, b) => 
    (b.priority_score || 0) - (a.priority_score || 0)
  );

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-white shadow-sm rounded-lg p-6 animate-pulse">
            <div className="flex items-center justify-between mb-3">
              <div className="h-6 bg-gray-200 rounded w-1/3"></div>
              <div className="h-6 bg-gray-200 rounded-full w-20"></div>
            </div>
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 rounded w-full"></div>
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            </div>
            <div className="mt-4 grid grid-cols-4 gap-3">
              {[1, 2, 3, 4].map((j) => (
                <div key={j} className="h-8 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (sortedHypotheses.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500 mb-4">{emptyMessage}</p>
        {showCreateButton && (
          <PermissionGate requireWrite>
            <button
              onClick={() => navigate(filterProblemId ? `/app-hypotheses/new?problem_id=${filterProblemId}` : '/app-hypotheses/new')}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              {createButtonText}
            </button>
          </PermissionGate>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {sortedHypotheses.map((hypothesis) => {
        const problemTitle = problems[hypothesis.problem_id];
        return (
          <HypothesisCard
            key={hypothesis.id}
            hypothesis={hypothesis}
            showProblemTitle={showProblemTitles}
            showProjectContext={showProjectContext}
            {...(problemTitle && { problemTitle })}
            {...(onHypothesisClick && { onClick: onHypothesisClick })}
            {...(onHypothesisUpdate && { onUpdate: onHypothesisUpdate })}
          />
        );
      })}
      
      {showCreateButton && sortedHypotheses.length > 0 && (
        <PermissionGate requireWrite>
          <div className="mt-6 text-center">
            <button
              onClick={() => navigate(filterProblemId ? `/app-hypotheses/new?problem_id=${filterProblemId}` : '/app-hypotheses/new')}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              {createButtonText}
            </button>
          </div>
        </PermissionGate>
      )}
    </div>
  );
};

export default HypothesesList;